import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "@/index.css";
import App from "@/App";

/**
 * BUILD_ID — bump this any time you deploy, so you can verify in devtools which build is live.
 * Logged once on app start.
 */
const BUILD_ID = `${(process.env.REACT_APP_ENV || 'prod').toUpperCase()} · wave42-471-ambassador-blitz`;
// eslint-disable-next-line no-console
console.log(`🚀 My Hometown Direct Care build: ${BUILD_ID}`);

/**
 * Global axios request interceptor.
 *   1. Sanitizes duplicate `/api/` segments so `${API}/api/foo` doesn't
 *      collapse to a `/api/api/foo` 404.
 *   2. Wave 42.157 — attach the JWT from localStorage as
 *      `Authorization: Bearer <token>` on every request. The backend
 *      relies on this header (verified JWT) instead of the legacy
 *      client-supplied `X-User-Id` header for identity.
 *   3. Wave 42.157 — auto-logout on 401 so a stale/expired token
 *      doesn't leave the UI in a confused half-authenticated state.
 */
axios.interceptors.request.use((config) => {
  if (config.url && config.url.includes("/api/api/")) {
    // eslint-disable-next-line no-console
    console.warn("[axios] Collapsing /api/api/ →", config.url);
    config.url = config.url.replace("/api/api/", "/api/");
  }
  try {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (_) { /* localStorage unavailable — proceed unauthenticated */ }
  return config;
});

axios.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    // Ignore 401s on the login/register/google endpoints themselves —
    // those are expected when credentials are wrong.
    const isAuthEndpoint = /\/api\/auth\/(login|register|admin-login|google|forgot-password|reset-password)/.test(url);
    if (status === 401 && !isAuthEndpoint) {
      try {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      } catch (_) { /* ignore */ }
      // Redirect to login unless we're already there.
      if (typeof window !== "undefined" && !/\/(login|admin-login|employer-login|patient-login|clinic-login)/.test(window.location.pathname)) {
        window.location.assign("/login");
      }
    }
    return Promise.reject(err);
  },
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
